import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { UnitEditMove } from "@shared/models/analytics/unit"
import type { Course } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import Patch from "@shared/models/patch-updater"
import { extractUnit } from "cc-frontend/lib/transferrable-date-utils"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  insertRange,
} from "cc-frontend/lib/unit-utils"
import { isCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { find, get, includes } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  unitId: string
  amount: number
  course: Course
  planbook: Planbook
  newStartDate: string
  rotationCalendar: RotationCalendar
}

type MovingCalendarPatch = PatchModel & {
  unitIdsOnTheMove?: string[]
  cardStackIdsOnTheMove?: Array<string | null>
}

export default {
  name: "MOVE_UNIT",

  params: {
    unitId: "string",
    amount: "number",
    course: "object",
    planbook: "object",
    newStartDate: "string",
    rotationCalendar: "object",
  },

  patches(payload: Payload) {
    let schoolDays = payload.rotationCalendar.attributes.schoolDays
    let unit = find(payload.course.attributes.calendar.units, {
      id: payload.unitId,
    })
    if (unit === undefined || unit === null) return []

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar
    )

    let { transferrableArray, courseDatesWithoutUnit } = extractUnit(
      payload.amount,
      unit,
      payload.course.attributes.calendar.dates,
      consolidatedCourseDatesOff,
      schoolDays
    )

    let newCourseDates = insertRange(
      transferrableArray,
      payload.newStartDate,
      payload.amount,
      unit,
      courseDatesWithoutUnit,
      consolidatedCourseDatesOff,
      schoolDays,
      true
    )

    try {
      let ret = constructUnitsAndDates(newCourseDates, consolidatedCourseDatesOff, schoolDays)

      let { courseDates, units } = ret

      let unitIdsOnTheMove = get(find(units, { id: payload.unitId }), "descendantIds") || []
      unitIdsOnTheMove = unitIdsOnTheMove.concat([payload.unitId])
      let cardStackIdsOnTheMove = pipe(
        courseDates,
        A.filter(isCourseDateCustom),
        A.filter((cd) => includes(cd.attributes.unitIds, payload.unitId)),
        A.map((cd) => cd.attributes.cardStackId)
      )

      let patch: MovingCalendarPatch = Patch.create("course", payload.course.id)
        .inc(`attributes.calendar.version`, 1)
        .set(`attributes.calendar.dates`, courseDates)
        .set(`attributes.calendar.units`, units).patch

      // @ts-ignore
      patch.unitIdsOnTheMove = unitIdsOnTheMove
      // @ts-ignore
      patch.cardStackIdsOnTheMove = cardStackIdsOnTheMove

      return [patch]
    } catch (e) {
      console.error(e)
      return []
    }
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("course", payload.course.id)
      .inc(`attributes.calendar.version`, 1)
      .set(
        "attributes.calendar.dates",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
      )
      .set(
        "attributes.calendar.units",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
      ).patch
    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let course = findFn("course", payload.course.id)
    let planbook = findFn("planbook", payload.planbook.id)
    let unit =
      findFn("card-stack", payload.unitId) || findFn("card-stack-summary", payload.unitId) || {}

    let unitTitle = get(unit, "attributes.title")
    let amount = Math.abs(payload.amount)
    let direction = payload.amount > 0 ? "forward" : "backwards"
    let dayOrDays = payload.amount === 1 ? "day" : "days"
    let newEvent: UnitEditMove = {
      ...createBlankEvent(),
      root_id: payload.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: null,
      primary_verb: "move",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),

      primary_object_type: "unit",
      primary_object_id: payload.unitId,
      primary_object_specifier: null,
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],

      adverbial_verb: "dragging_dropping",

      adverbial_object_type: "unit",
      adverbial_object_id: payload.unitId,
      adverbial_object_specifier: null,
      adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    }
    return {
      title: "Moved Unit",
      titleForAnalytics: "Unit - Move",
      description: `Moved "${unitTitle}" ${direction} ${amount} ${dayOrDays}`,
      context: {
        unitTitle: get(unit, "attributes.title"),
        courseTitle: get(course, "attributes.title"),
        planbookTitle: get(planbook, "attributes.title"),
        unitId: get(unit, "id"),
        courseId: get(course, "id"),
        planbookId: get(planbook, "id"),
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `unit-${payload.unitId}`,
        to: [`course-edits:course-${course.id}`, `planbook-edits:planbook-${planbook.id}`],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}
